import { NgClass } from '@angular/common';
import { Component, computed, effect, ElementRef, input, signal, viewChild } from '@angular/core';
import { decode } from 'blurhash';

@Component({
  selector: 'mtjr-blur-img',
  imports: [NgClass],
  template: `
    <div [class]="containerClass() + ' relative'">
      <!-- Blurhash canvas placeholder -->
      <canvas
        #blurhashCanvas
        [class]="'absolute inset-0 z-0 ' + (canvasClass() ?? imgClass())"
        [ngClass]="{ hidden: !showCanvas() }"
      ></canvas>
      @if (proxiedSrc()) {
        <img
          [src]="proxiedSrc()"
          [alt]="alt()"
          [class]="imgClass() + ' transition-opacity duration-700 '"
          [class.opacity-0]="!imageLoaded() && blurHash()"
          [class.opacity-100]="imageLoaded() || !blurHash()"
          (load)="onImageLoad()"
          (error)="onImageError($event)"
          [ngClass]="{
            'z-10': imageLoaded(),
            sticky: imageLoaded(),
          }"
          fetchpriority="high"
        />
      }
    </div>
  `,
})
export class VxBlurImgComponent {
  containerClass = input('');
  canvasClass = input<string>();
  imgClass = input('');
  showCanvas = signal<boolean>(true);
  src = input.required<string | undefined | null>();
  proxy = input<string | undefined>('https://dev-img.mtjr.ly/unsafe/resize:auto:1280:720/sm:1/q:85/plain/');
  proxiedSrc = computed(() => {
    const proxy = this.proxy();
    const src = this.src();
    return proxy && src ? proxy + src : src || undefined;
  });

  alt = input('');
  blurHash = input<string | undefined | null>();

  // Blurhash and image loading state
  imageLoaded = signal(false);
  blurhashCanvas = viewChild<ElementRef<HTMLCanvasElement>>('blurhashCanvas');

  // Retry logic for image loading
  private retryCount = 0;
  private readonly maxRetries = 1;

  constructor() {
    effect(() => {
      if (this.imageLoaded()) {
        setTimeout(() => {
          this.showCanvas.set(false);
        }, 1000);
      }
    });

    // Effect to render blurhash when blurHash or src changes
    effect(() => {
      const blurHash = this.blurHash();
      const canvas = this.blurhashCanvas()?.nativeElement;

      if (blurHash && canvas) {
        this.renderBlurhash(blurHash, canvas);
      }

      // Reset image loaded state when src changes
      this.imageLoaded.set(false);
      this.retryCount = 0; // Reset retry count when src changes
    });
  }

  onImageLoad() {
    this.imageLoaded.set(true);
    this.retryCount = 0; // Reset retry count on successful load
  }

  onImageError(event: Event) {
    console.log(`Image load failed`);
    const imgElement = event.target as HTMLImageElement;

    if (this.retryCount < this.maxRetries) {
      this.retryCount++;

      // Retry with exponential backoff
      const delay = Math.pow(2, this.retryCount - 1) * 1000; // 1s, 2s, 4s

      setTimeout(() => {
        if (imgElement && this.src()) {
          // Force reload by adding a timestamp parameter
          const originalSrc = this.src();
          const separator = originalSrc?.includes('?') ? '&' : '?';
          imgElement.src = `${originalSrc}${separator}_retry=${Date.now()}`;
        }
      }, delay);
    } else {
      console.warn(`Image failed to load after ${this.maxRetries} retries:`, this.src());
      this.imageLoaded.set(true); // Hide blurhash even on final error
    }
  }

  private renderBlurhash(blurhash: string, canvas: HTMLCanvasElement) {
    try {
      const width = 32;
      const height = 32;

      canvas.width = width;
      canvas.height = height;

      const pixels = decode(blurhash, width, height);
      const ctx = canvas.getContext('2d');

      if (ctx) {
        const imageData = ctx.createImageData(width, height);
        imageData.data.set(pixels);
        ctx.putImageData(imageData, 0, 0);
      }
    } catch (error) {
      console.warn('Failed to render blurhash:', error);
    }
  }
}
